/** "The visit ran short and I have an hour — who else is near here?"
 *
 *  The plan is built against a capacity set on Sunday. It cannot know that Tuesday's
 *  first meeting finished early, so the rep needs to reopen the week from where they
 *  are standing rather than from the top of a ranked list.
 *
 *  Fetched on OPEN, never with the plan: it is a question about one trip, asked rarely,
 *  and answering it for every trip on every plan load would be work nobody asked for.
 */
import React, { useCallback, useState } from 'react';
import type { PlanNearbyClient } from '../../api/types';

export interface SalesPlanAddNearbyProps {
  /** Which trip to measure from. Null is the CALL list, which has no geography. */
  tripNo: number | null;
  onFind: (tripNo: number | null) => Promise<PlanNearbyClient[]>;
  onAdd: (clientId: string, tripNo: number | null) => void;
  disabled?: boolean;
}

const where = (client: PlanNearbyClient): string =>
  [client.city, client.state].filter(Boolean).join(', ');

/** Distance is the whole point of the trip list, so it leads. On the call list there is
 *  none, and the last touch is what tells the rep whether the call is worth making. */
const detail = (client: PlanNearbyClient): string => {
  const parts: string[] = [];
  if (client.miles != null) {
    if (client.miles < 1) {
      parts.push('same spot');
    } else if (client.drive_min != null) {
      parts.push(`${client.drive_min} min drive (${client.miles.toFixed(1)} mi)`);
    } else {
      parts.push(`${client.miles.toFixed(1)} mi away`);
    }
    if (client.over_drive_limit) parts.push('longer than your trip drive limit');
  } else if (client.days_since_last_cse_contact != null) {
    parts.push(`${Math.round(client.days_since_last_cse_contact)} days since contact`);
    parts.push(client.days_since_last_cse_live_contact != null
      ? `${Math.round(client.days_since_last_cse_live_contact)} since live`
      : 'no live contact');
    parts.push(client.days_since_last_cse_in_person_contact != null
      ? `${Math.round(client.days_since_last_cse_in_person_contact)} since visit`
      : 'no visit');
  }
  const place = where(client);
  if (place) parts.push(place);
  return parts.join(' · ');
};

export const SalesPlanAddNearby: React.FC<SalesPlanAddNearbyProps> = ({
  tripNo, onFind, onAdd, disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<PlanNearbyClient[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isCalls = tripNo === null;

  const toggle = useCallback(async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    // Refetched every time it opens. The week moves as the rep works it, and a list of
    // people they have already added is worse than no list.
    setLoading(true);
    setError(null);
    try {
      setClients(await onFind(tripNo));
    } catch {
      setClients(null);
      setError('Could not load who is nearby.');
    } finally {
      setLoading(false);
    }
  }, [open, onFind, tripNo]);

  const label = isCalls ? 'Add a call' : 'Add a stop nearby';

  return (
    <div className="spc-add">
      <button
        className="spc-add-toggle"
        aria-expanded={open}
        disabled={disabled}
        onClick={toggle}
      >
        + {label}
      </button>

      {open ? (
        <div className="spc-add-list" role="group" aria-label={label}>
          {loading ? <div className="spc-add-note">Looking…</div> : null}
          {error ? <div className="spc-add-note spc-add-error">{error}</div> : null}
          {!loading && !error && clients && !clients.length ? (
            <div className="spc-add-note">
              {isCalls
                ? 'Nobody left to call this week.'
                : 'Nobody else close enough to be worth the detour.'}
            </div>
          ) : null}
          {(clients ?? []).map((client) => (
            <button
              key={client.client_id}
              className="spc-add-item"
              disabled={disabled}
              onClick={() => onAdd(client.client_id, tripNo)}
            >
              <span className="spc-add-name">{client.client_name ?? client.client_id}</span>
              <span className="spc-add-detail">{detail(client)}</span>
              {client.opportunities?.length ? (
                <span className="spc-add-opp">{client.opportunities.join(' · ')}</span>
              ) : null}
              {client.whitespace?.length ? (
                <span className="spc-add-whitespace">{client.whitespace.join(' · ')}</span>
              ) : null}
              {client.tier ? <span className="spc-add-tier">{client.tier}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
